import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

const Properties = lazy(() => import("./views/Properties.js"));
const Account = lazy(() => import("./views/Account.js"));
const Collection = lazy(() => import("./views/Collection.js"));
const Rooms = lazy(() => import("./views/Rooms.js"));
const EditCollection = lazy(() => import("./views/EditCollection"));
const CreateCollection = lazy(() => import("./views/CreateCollection"));
const ManageWebsite = lazy(() =>
	import("./views/ManageWebsite/ManageWebsite.js").then((module) => ({
		default: module.ManageWebsite,
	})),
);
const CreateProperty = lazy(() => import("./views/CreateProperty"));
const AutogenerateProperty = lazy(() => import("./views/AutogenerateProperty"));
const GuidedReview = lazy(() => import("./views/GuidedReview"));
const PropertyEditor = lazy(() => import("./views/PropertyEditor"));
const ReportProblemReservation = lazy(
	() => import("./views/ReportProblemReservation"),
);
const CreateReservation = lazy(() => import("./views/CreateReservation.js"));
const DashboardEarnings = lazy(() => import("./views/DashboardEarnings"));
const DashboardRevenue = lazy(() => import("./views/DashboardRevenue"));
const Reports = lazy(() => import("./views/Reports"));
const ReportsPerformance = lazy(() => import("./views/ReportsPerformance"));
const ReportsChannels = lazy(() => import("./views/ReportsChannels"));
const ReportsTaxes = lazy(() => import("./views/ReportsTaxes"));
const ReportsAvailability = lazy(() => import("./views/ReportsAvailability"));
const ReportsChangesAudit = lazy(() => import("./views/ReportsChangesAudit"));
const ReportsValidations = lazy(() => import("./views/ReportsValidations"));
const Organizations = lazy(() => import("./views/Organizations.js"));
const CreateOrganization = lazy(() => import("./views/CreateOrganization.js"));
const EditOrganization = lazy(() => import("./views/EditOrganization.js"));
const OrganizationDetails = lazy(
	() => import("./views/OrganizationDetails.js"),
);
const Users = lazy(() => import("./views/OrgUsers.js"));
const AdminUsers = lazy(() => import("./views/UsersList.js"));
const EditUser = lazy(() => import("./views/EditUser.js"));
const PromotionsList = lazy(() => import("./views/PromotionsList.js"));
const CreatePromotion = lazy(() => import("./content/promotions/CreatePromotion.js"));
const EditPromotions = lazy(() => import("./content/promotions/EditPromotions.js"));
const GlobalFeeSchedule = lazy(() => import("./views/GlobalFeeSchedule"));
const Cleaning = lazy(() => import("./views/Cleaning"));
const Faq = lazy(() => import("./views/Faq"));
const Dashboard = lazy(() => import("./views/Dashboard"));
const KnowledgeBot = lazy(() => import("./views/KnowledgeBot"));
const SupportTickets = lazy(() => import("./views/SupportTickets.js"));
const CommunicationSettings = lazy(() => import("./views/CommunicationSettings"));
const FailedBookings = lazy(() => import("./views/FailedBookings.js"));
const FailedBookingForm = lazy(() => import("./views/FailedBookingForm.js"));

import { PermissionGate } from "../helpers/helpers";
import { ROUTES } from "../js/constants";
import Loader from "./global/Loader";

const Components = {
	Properties,
	Account,
	ManageWebsite,
	CreateProperty,
	AutogenerateProperty,
	GuidedReview,
	PropertyEditor,
	ReportProblemReservation,
	CreateReservation,
	DashboardEarnings,
	DashboardRevenue,
	Reports,
	ReportsPerformance,
	ReportsChannels,
	ReportsTaxes,
	ReportsAvailability,
	ReportsChangesAudit,
	ReportsValidations,
	Collection,
	Rooms,
	EditCollection,
	CreateCollection,
	Organizations,
	CreateOrganization,
	EditOrganization,
	OrganizationDetails,
	Users,
	AdminUsers,
	EditUser,
	PromotionsList,
	CreatePromotion,
	EditPromotions,
	GlobalFeeSchedule,
	Cleaning,
	Faq,
	Dashboard,
	KnowledgeBot,
	SupportTickets,
	CommunicationSettings,
	FailedBookings,
	FailedBookingForm,
};
const buildPermissionRoutes = (permissions = []) => {
	const elems = [];
	// Cache elements by component+permissions so routes sharing the same component
	// (e.g. /properties/:id/edit and /collections/:id/edit both rendering PropertyEditor)
	// reuse the same React element reference. This prevents React from unmounting and
	// remounting the component when navigating between those routes.
	const elementCache = {};
	ROUTES.forEach((r, idx) => {
		const Comp = Components[r.component];
		if (!Comp) return;
		const cacheKey = `${r.component}:${[...(r.required || [])].sort().join(",")}`;
		if (!elementCache[cacheKey]) {
			elementCache[cacheKey] = (
				<PermissionGate
					key={`pg-${cacheKey}`}
					permissions={permissions}
					required={r.required}
					mode={r.mode || "any"}
					fallback={<Navigate to="/account/profile" replace />}>
					<Comp />
				</PermissionGate>
			);
		}
		elems.push(<Route key={idx} path={r.path} element={elementCache[cacheKey]} />);
	});
	// default landing: dashboard if allowed, otherwise account/profile
	const hasDashboard = permissions.length >= 0; // always allow landing
	elems.push(
		<Route
			key="root-redirect"
			path="/"
			element={
				<Navigate
					to={hasDashboard ? "/dashboard" : "/account/profile"}
					replace
				/>
			}
		/>,
	);
	return elems;
};

export const Main = (props) => {
	const permissions = props.permissions || [];
	let routes = buildPermissionRoutes(permissions);
	return (
		<main className={props.mobileMode ? "mobile" : "desktop"}>
			<Suspense fallback={<Loader />}>
				<Routes>
					{routes}
					<Route
						path="*"
						element={<Navigate to="/account/profile" replace />}
					/>
				</Routes>
			</Suspense>
		</main>
	);
};
